/**
 * @fileoverview React bindings for `@puffin-core/api-client` — imported via the
 * `./react` subpath so the root package stays framework-free (the
 * `@puffin-core/error-handling/react` precedent).
 *
 * @module @puffin-core/api-client/react
 */

import { useCallback, useRef } from 'react';

import { createSubmitIdempotencyKeySource } from './submitIdempotencyKey.js';

/**
 * Returns `nextKey(payload)` — the `Idempotency-Key` to send for this submit
 * (IDEM-HTTP-001). The source lives in a `useRef` — untouched by rendering —
 * so the key survives re-renders and retries, and re-mints only when the
 * SUBMITTED PAYLOAD changes. Full lifetime rationale:
 * `lib/esm/submitIdempotencyKey.js`.
 *
 * Call it INSIDE the submit handler, never in a render body.
 *
 * @param {string} namespace - Short operation name, prefixed onto every key.
 * @returns {(payload: *) => string}
 */
export function useSubmitIdempotencyKey(namespace) {
  const sources = useRef(new Map());

  return useCallback(
    (payload) => {
      if (!sources.current.has(namespace)) {
        sources.current.set(namespace, createSubmitIdempotencyKeySource(namespace));
      }
      return sources.current.get(namespace)(payload);
    },
    [namespace],
  );
}

export default useSubmitIdempotencyKey;

// WEB-APP-VERSION-REFRESH-001: hold the page's reload-safety gate shut while reloading would lose work.
export { useReloadBlocker } from './useReloadBlocker.js';
